// UserManagementHelp.tsx
import React, { useMemo, useRef, useState } from "react";
import {
  Drawer,
  IconButton,
  TextField,
  Typography,
  Box,
  Divider,
  Chip,
  Stack,
  List,
  ListItem,
  ListItemText,
  Tooltip,
  Grid,
  Card,
  CardContent,
  Stepper,
  Step,
  StepLabel,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  Menu,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import GroupIcon from "@mui/icons-material/Group";
import SecurityIcon from "@mui/icons-material/Security";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import PersonIcon from "@mui/icons-material/Person";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';


type PDFLink = { title: string; href: string };
type StepBlock = { title: string; bullets: string[] };
type HierarchyLevel = { level: string; createdBy: string; desc: string };

const DEFAULT_PDFS = [
  {
    title: "User Management Module Manual",
    href: "https://mdimages-test.s3.amazonaws.com/50684ec2-6e0c-4008-a92c-164f86d162aa_User_Management_Module_Manual.pdf",
  },
];

// ---- Static Content ----

const HIERARCHY_LEVELS: HierarchyLevel[] = [
  { level: "Organisation", createdBy: "Machadalo Super Admin (Super Admin)", desc: "Top-level entity — company, institution, or business." },
  { level: "Profile", createdBy: "Machadalo Super Admin or Admin Account", desc: "Defines a permission set (role) for users." },
  { level: "Admin Account", createdBy: "Machadalo Super Admin", desc: "First user account for a new organisation." },
  { level: "Executive Accounts", createdBy: "Organisation's Admin Account", desc: "Regular user accounts created by the admin." },
];

const PROFILE_CREATION_STEPS: StepBlock[] = [
  { title: "Open Manage Profile", bullets: ["Go to Management → Manage Profile in the left sidebar."] },
  { title: "View Existing Profiles", bullets: ["The page lists all profiles already created for the organisation."] },
  { title: "Click Add", bullets: ["Click the Add button to open the new profile form."] },
  { title: "Enter Profile Name", bullets: ["Choose a clear, descriptive name (e.g., 'Admin', 'Sales Executive')."] },
  { title: "Set Session Timeout", bullets: ["Select the inactivity duration after which users are auto-signed out."] },
  { title: "Configure Permissions", bullets: ["Select the specific features and data this profile should access."] },
  { title: "Save", bullets: ["Profile is created and ready to be assigned to users."] },
];

const USER_CREATION_STEPS: StepBlock[] = [
  { title: "Open Manage Users", bullets: ["Go to Management → Users → View."] },
  { title: "Click Add", bullets: ["Click Add to open the new user form."] },
  { title: "Fill in User Details", bullets: ["Complete all fields — First Name, Last Name, User Name, Email ID, Mobile Number, Organisation, Profile."] },
  { title: "Click Create", bullets: ["Click Create to save the new user account."] },
  { title: "Confirm Creation", bullets: ["The user appears in the Users list."] },
];

const USER_FORM_FIELDS = [
  { field: "First Name", required: "Yes", notes: "The user's given name." },
  { field: "Last Name", required: "Yes", notes: "The user's family name." },
  { field: "User Name", required: "Yes", notes: "The login identifier for the user." },
  { field: "Email ID", required: "Yes — Important", notes: "Used to set password on first login and send OTP for every login. Must be valid and accessible." },
  { field: "Mobile Number", required: "Yes — Important", notes: "Used for WhatsApp trigger notifications (e.g., lead assignment). Must be a valid WhatsApp-enabled number." },
  { field: "Organisation", required: "Yes", notes: "The organisation this user belongs to." },
  { field: "Profile", required: "Yes", notes: "The profile assigned to this user, determining permissions." },
];

const SectionHeader: React.FC<{ title: string; subtitle?: string; id?: string }> = ({ title, subtitle, id }) => (
  <Box id={id} sx={{ mb: 1.25, mt: 0 }}>
    <Typography variant="subtitle1" fontWeight={700}>
      {title}
    </Typography>
    {subtitle && (
      <Typography variant="caption" color="text.secondary">
        {subtitle}
      </Typography>
    )}
  </Box>
);

const StepAccordion: React.FC<{ steps: StepBlock[]; label: string; defaultExpanded?: boolean }> = ({
  steps,
  label,
  defaultExpanded,
}) => {
  const [active, setActive] = useState(0);
  return (
    <Accordion defaultExpanded={defaultExpanded} disableGutters sx={{ mb: 2, borderRadius: 2 }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography fontWeight={600}>
          {label} ({steps.length})
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Stepper activeStep={active} alternativeLabel sx={{ mb: 2, flexWrap: "wrap" }}>
          {steps.map((s, i) => (
            <Step key={s.title} onClick={() => setActive(i)}>
              <StepLabel>{s.title}</StepLabel>
            </Step>
          ))}
        </Stepper>
        {steps.map((s, idx) => {
          const isActive = idx === active;
          return (
            <Box
              key={s.title}
              sx={{
                mb: 1.25,
                p: 1.25,
                border: (t) => `1px dashed ${t.palette.divider}`,
                borderRadius: 2,
                bgcolor: (t) => (isActive ? t.palette.action.hover : "transparent"),
              }}
            >
              <Typography fontWeight={600} sx={{ mb: 0.5 }}>
                {idx + 1}. {s.title}
              </Typography>
              <List dense sx={{ pt: 0 }}>
                {s.bullets.map((b, j) => (
                  <ListItem key={j} sx={{ py: 0.2 }}>
                    <ListItemText primaryTypographyProps={{ variant: "body2" }} primary={`• ${b}`} />
                  </ListItem>
                ))}
              </List>
            </Box>
          );
        })}
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" onClick={() => setActive((p) => Math.max(0, p - 1))}>
            Previous
          </Button>
          <Button variant="contained" onClick={() => setActive((p) => Math.min(steps.length - 1, p + 1))}>
            Next
          </Button>
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};

const UserManagementHelp: React.FC<{ pdfs?: PDFLink[] }> = ({ pdfs }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [menuEl, setMenuEl] = useState<null | HTMLElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  const jumpTo = (hash: string) => {
    const el = contentRef.current?.querySelector(hash);
    if (el) (el as HTMLElement).scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleOpenMenu = (e: React.MouseEvent<HTMLElement>) => setMenuEl(e.currentTarget);
  const handleCloseMenu = () => setMenuEl(null);
  const handlePrint = () => {
    window.open(DEFAULT_PDFS[0].href, "_blank");
  };

  const visiblePdfs = pdfs && pdfs.length > 0 ? pdfs : DEFAULT_PDFS;

  const sectionTexts = useMemo(() => {
    const flatten = (steps: StepBlock[]) => steps.map((s) => `${s.title} ${s.bullets.join(" ")}`).join(" ");

    return {
      overview: "user management profile account permission set login access organisation",
      hierarchy: "organisation profile admin account executive account Machadalo Super Admin",
      createProfile: flatten(PROFILE_CREATION_STEPS) + " profile name session timeout permissions clone",
      createUser: flatten(USER_CREATION_STEPS) + " first name last name email mobile number",
      userForm: "email id mobile number important password setup otp whatsapp trigger notification",
    };
  }, []);

  const q = query.trim().toLowerCase();
  const matches = (sectionKey: keyof typeof sectionTexts) =>
    !q || sectionTexts[sectionKey].toLowerCase().includes(q);

  return (
    <div>
      <Tooltip title="User Management Help">
        <IconButton onClick={() => setIsOpen(true)}>
          <HelpOutlineIcon fontSize="medium" />
        </IconButton>
      </Tooltip>

      <Drawer anchor="right" open={isOpen} onClose={() => setIsOpen(false)} sx={{ zIndex: 1400 }}>
        <Box
          sx={{
            width: {xs:'fit-content' , sm:560},
            display: "flex",
            flexDirection: "column",
            height: "100%",
            bgcolor: (t) => (t.palette.mode === "light" ? "#fff" : "background.paper"),
          }}
        >
          {/* Header */}
          <Box
            sx={{
              px: 2,
              py: 1.25,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              borderBottom: (t) => `1px solid ${t.palette.divider}`,
              position: "sticky",
              top: 0,
              zIndex: 2,
              background: "linear-gradient(90deg, rgba(99,102,241,0.16), rgba(14,165,233,0.16))",
              backdropFilter: "saturate(1.2)",
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <SecurityIcon />
              <Typography variant="h6" fontWeight={800}>
                User Management • Help
              </Typography>
            </Stack>

            <Stack direction="row" spacing={1}>
              <Menu
                anchorEl={menuEl}
                open={Boolean(menuEl)}
                onClose={handleCloseMenu}
              >
                {visiblePdfs.map((p) => (
                  <MenuItem
                    key={p.title}
                    component={p.href ? "a" : "li"}
                    href={p.href || undefined}
                    target={p.href ? "_blank" : undefined}
                    rel={p.href ? "noopener noreferrer" : undefined}
                    onClick={handleCloseMenu}
                  >
                    <PictureAsPdfIcon fontSize="small" style={{ marginRight: 8 }} />
                    {p.title}
                  </MenuItem>
                ))}
              </Menu>
              <Tooltip title="Print / Save as PDF">
                <IconButton onClick={handlePrint}>
                  <PictureAsPdfIcon />
                </IconButton>
              </Tooltip>
              <IconButton onClick={() => setIsOpen(false)}>
                <CloseIcon />
              </IconButton>
            </Stack>
          </Box>

          {/* Search + Quick Jump */}
          <Box sx={{ px: 2, pt: 2 }}>
            <TextField
              size="small"
              fullWidth
              placeholder="Search profiles, users, permissions, email, mobile…"
              InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1 }} /> }}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            <Stack direction="row" spacing={1} sx={{ my: 1, flexWrap: "wrap" , rowGap:'8px' , columnGap:'5px'}}>
              <Chip label="Overview" size="small" variant="outlined" onClick={() => jumpTo("#sec-overview")} sx={{'&.MuiButtonBase-root.MuiChip-root': {m:0}}}/>
              <Chip label="Hierarchy" size="small" variant="outlined" onClick={() => jumpTo("#sec-hierarchy")} sx={{'&.MuiButtonBase-root.MuiChip-root': {m:0}}} />
              <Chip label="Create Profile" size="small" variant="outlined" onClick={() => jumpTo("#sec-create-profile")} sx={{'&.MuiButtonBase-root.MuiChip-root': {m:0}}} />
              <Chip label="Create User" size="small" variant="outlined" onClick={() => jumpTo("#sec-create-user")} sx={{'&.MuiButtonBase-root.MuiChip-root': {m:0}}} />
              <Chip label="User Form" size="small" variant="outlined" onClick={() => jumpTo("#sec-user-form")} sx={{'&.MuiButtonBase-root.MuiChip-root': {m:0}}} />
            </Stack>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Scrollable Content */}
          <Box ref={contentRef} sx={{ px: 2, pb: 3, overflowY: "auto" }}>
            {/* Overview */}
            {matches("overview") && (
              <>
                <SectionHeader id="sec-overview" title="Overview" subtitle="Creation and administration of Profiles and User Accounts." />
                <Card variant="outlined" sx={{ borderRadius: 2, p: 1.5, mb: 1 }}>
                  <Grid container spacing={1}>
                    <Grid item xs={6}>
                      <Card variant="outlined" sx={{ p: 1.5, borderRadius: 2, bgcolor: "action.hover" }}>
                        <Typography fontWeight={600}>Profile</Typography>
                        <Typography variant="body2">A reusable permission set (role) that determines which features and data a user can access.</Typography>
                      </Card>
                    </Grid>
                    <Grid item xs={6}>
                      <Card variant="outlined" sx={{ p: 1.5, borderRadius: 2, bgcolor: "action.hover" }}>
                        <Typography fontWeight={600}>User Account</Typography>
                        <Typography variant="body2">An individual login, assigned to exactly one Profile, used by a specific person.</Typography>
                      </Card>
                    </Grid>
                  </Grid>
                </Card>
              </>
            )}

            {/* Hierarchy */}
            {matches("hierarchy") && (
              <>
                <SectionHeader id="sec-hierarchy" title="Account Hierarchy" subtitle="Four-level hierarchy, each created under the one above it." />
                <Grid container spacing={1}>
                  {HIERARCHY_LEVELS.map((level) => (
                    <Grid item xs={12} key={level.level}>
                      <Card variant="outlined" sx={{ borderRadius: 2 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 2, py: 1, borderBottom: (t) => `1px solid ${t.palette.divider}` }}>
                          {level.level === "Organisation" && <GroupIcon />}
                          {level.level === "Profile" && <AdminPanelSettingsIcon />}
                          {level.level === "Admin Account" && <SecurityIcon />}
                          {level.level === "Executive Accounts" && <PersonIcon />}
                          <Typography fontWeight={600}>{level.level}</Typography>
                        </Box>
                        <CardContent sx={{ pt: 1 }}>
                          <Typography variant="body2" sx={{ mb: 0.5 }}>
                            <strong>Created by:</strong> {level.createdBy}
                          </Typography>
                          <Typography variant="body2">{level.desc}</Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
                <Alert severity="info" sx={{ mt: 1 }}>
                  Only the very first Admin Profile and Admin User need to be set up from Machadalo Super Admin — everything afterward is self-service for the organisation's admin.
                </Alert>
              </>
            )}

            {/* Create Profile */}
            {matches("createProfile") && (
              <>
                <SectionHeader id="sec-create-profile" title="Creating a Profile" subtitle="Defines what a user assigned to it can see and do." />
                <StepAccordion steps={PROFILE_CREATION_STEPS} label="Profile Creation Steps" defaultExpanded />
                <Card variant="outlined" sx={{ borderRadius: 2, p: 1.5, mt: 1 }}>
                  <Typography fontWeight={600} sx={{ mb: 0.5 }}>Cloning a Profile</Typography>
                  <Typography variant="body2">
                    Instead of building a new profile from scratch, you can clone an existing one. On the Manage Profile list, locate the profile, click Action, and select Clone.
                  </Typography>
                </Card>
              </>
            )}

            {/* Create User */}
            {matches("createUser") && (
              <>
                <SectionHeader id="sec-create-user" title="Creating a User" subtitle="Individual login assigned to a specific Profile." />
                <StepAccordion steps={USER_CREATION_STEPS} label="User Creation Steps" />
              </>
            )}

            {/* User Form Fields */}
            {matches("userForm") && (
              <>
                <SectionHeader id="sec-user-form" title="User Form — Field Reference" />
                <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, mb: 1 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: "action.hover" }}>
                        <TableCell sx={{ fontWeight: 700 }}>Field</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Required</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Notes</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {USER_FORM_FIELDS.map((row) => (
                        <TableRow key={row.field}>
                          <TableCell>{row.field}</TableCell>
                          <TableCell>{row.required}</TableCell>
                          <TableCell>{row.notes}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Alert severity="warning" sx={{ mt: 1 }}>
                  <strong>Why Email ID and Mobile Number matter:</strong> Email ID is used for password setup and OTP login. Mobile Number is used for WhatsApp trigger notifications.
                </Alert>
              </>
            )}

            {/* Footer */}
            <Box sx={{ mt: 2, px: 1.5, py: 1, borderTop: (t) => `1px solid ${t.palette.divider}`, borderRadius: 2 }}>
              <Typography variant="caption" color="text.secondary">
                Tip: Use the Download icon in the header to fetch the full manual PDF, or print this page to PDF.
              </Typography>
            </Box>
          </Box>
        </Box>
      </Drawer>
    </div>
  );
};

export default UserManagementHelp;